/**
 * VPR — assessment detail. v0.6 UX: workflow stepper, band-colored rating
 * banner, state chips, accented section cards, two-list asset mapping with
 * in-place "mark vendor-supplied" flagging.
 * Route: /projects/:projectId/vendor-readiness/:assessmentId
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import { apiUrl } from '../api/client';

interface Finding { severity: string; code: string; qid?: string; message: string }
interface Detail {
  assessment: {
    id: string; vendor_name: string; product_in_scope: string | null; state: string;
    cycle_number: number; issued_fingerprint: string | null;
    validation_report: { findings?: Finding[] } | null;
  };
  answers: Array<{ qid: string; response: string | null; details: string | null }>;
  scores: Array<{ qid: string; score: string | null; suggested: number | null }>;
  rating: string;
  result: { gateFailures: string[]; achievedWeighted: number; maxWeighted: number; band: string | null };
  fullyAddressed: boolean;
  linkedAssetIds: string[];
}
interface Question { qid: string; section: string; question: string; guidance_assessor: string; weight: number; is_gate: number }
interface VendorAsset {
  asset_id: string; name: string; asset_type: string | null;
  vendor_readiness: string | null; active_engagement: string | null; linked: number;
}
interface CandidateAsset { asset_id: string; name: string; asset_type: string | null }
interface AssetLists { vendorSupplied: VendorAsset[]; candidates: CandidateAsset[] }

const SCORE_OPTIONS = ['3', '2', '1', '0', 'NA'] as const;
const LEVELS = ['Very_high', 'High', 'Moderate', 'Low', 'Very_low'] as const;

const BAND_STYLE: Record<string, string> = {
  VERY_HIGH: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  HIGH: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  MODERATE: 'bg-amber-50 text-amber-800 border-amber-200',
  LOW: 'bg-orange-50 text-orange-800 border-orange-200',
  VERY_LOW: 'bg-red-50 text-red-700 border-red-200',
};
const STATE_CHIP: Record<string, string> = {
  DRAFT: 'bg-ink-100 text-ink-700', ISSUED: 'bg-sky-50 text-sky-700',
  IMPORT_FAILED: 'bg-red-50 text-red-700', IMPORTED: 'bg-sky-50 text-sky-700',
  UNDER_REVIEW: 'bg-amber-50 text-amber-800', INVALID: 'bg-red-50 text-red-700',
  CONFIRMED: 'bg-emerald-50 text-emerald-700', FED: 'bg-emerald-100 text-emerald-800',
  REOPENED: 'bg-ink-100 text-ink-700',
};
const STAGES = ['Issue', 'Capture', 'Map assets', 'Score', 'Confirm', 'Feed'] as const;
function stageIndex(state: string, linked: number): number {
  switch (state) {
    case 'DRAFT': return 0;
    case 'ISSUED': case 'REOPENED': case 'IMPORT_FAILED': return 1;
    case 'IMPORTED': return linked > 0 ? 3 : 2;
    case 'UNDER_REVIEW': return 3;
    case 'CONFIRMED': return linked > 0 ? 5 : 2;
    case 'FED': return 6;
    default: return 0;
  }
}

function Card(props: { n?: string; title: string; children: React.ReactNode; tone?: 'default' | 'success' }) {
  return (
    <section className={`border rounded-sm bg-white ${props.tone === 'success' ? 'border-emerald-200' : 'border-ink-100'}`}>
      <div className="px-4 py-2.5 border-b border-ink-100 bg-ink-50/60 flex items-center gap-2">
        {props.n && (
          <span className="w-5 h-5 rounded-full bg-ink-900 text-white text-[11px] flex items-center justify-center font-medium">
            {props.n}
          </span>
        )}
        <span className="text-[11px] font-medium text-ink-600 uppercase tracking-wider">{props.title}</span>
      </div>
      <div className="p-4 space-y-3">{props.children}</div>
    </section>
  );
}

const th = 'text-left text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2';

export function VendorReadinessDetailPage() {
  const { projectId, assessmentId } = useParams<{ projectId: string; assessmentId: string }>();
  const qc = useQueryClient();
  const base = `/projects/${projectId}/vendor-readiness/${assessmentId}`;
  const key = ['vendor-readiness', projectId, assessmentId];
  const [msg, setMsg] = useState<string | null>(null);
  const [ackGates, setAckGates] = useState(false);
  const [engagement, setEngagement] = useState<string>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectionDirty, setSelectionDirty] = useState(false);

  const detailQ = useQuery({ queryKey: key, queryFn: () => api.get<Detail>(base) });
  const questionsQ = useQuery({
    queryKey: ['vendor-readiness-questions', projectId],
    queryFn: () => api.get<Question[]>(`/projects/${projectId}/vendor-readiness/questions`),
  });
  const assetsQ = useQuery({
    queryKey: [...key, 'vendor-supplied-assets'],
    queryFn: () => api.get<AssetLists>(`${base}/vendor-supplied-assets`),
  });

  useEffect(() => {
    if (detailQ.data && !selectionDirty) setSelected(new Set(detailQ.data.linkedAssetIds));
  }, [detailQ.data, selectionDirty]);

  const invalidate = () => { void qc.invalidateQueries({ queryKey: key }); };
  const invalidateAssets = () => { void qc.invalidateQueries({ queryKey: [...key, 'vendor-supplied-assets'] }); };
  const onErr = (e: unknown) => setMsg(e instanceof ApiError ? e.message : 'Request failed');

  const uploadM = useMutation({
    mutationFn: async (file: File) => {
      const bytes = new Uint8Array(await file.arrayBuffer());
      let bin = '';
      for (let i = 0; i < bytes.length; i += 0x8000)
        bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      return api.post(`${base}/submission`, { file_base64: btoa(bin) });
    },
    onSuccess: () => { setMsg('Submission imported.'); invalidate(); },
    onError: onErr,
  });
  const flagM = useMutation({
    mutationFn: (assetId: string) => api.post(`${base}/vendor-supplied-assets/${assetId}`, {}),
    onSuccess: () => { setMsg('Asset marked vendor-supplied — it can be mapped now.'); invalidateAssets(); },
    onError: onErr,
  });
  const saveLinksM = useMutation({
    mutationFn: () => api.put(`${base}/assets`, { asset_ids: [...selected] }),
    onSuccess: () => { setMsg('Asset mapping saved.'); setSelectionDirty(false); invalidate(); invalidateAssets(); },
    onError: onErr,
  });
  const scoreM = useMutation({
    mutationFn: (p: { qid: string; score: string | null }) => api.patch(`${base}/scores`, { scores: [p] }),
    onSuccess: invalidate, onError: onErr,
  });
  const reviewM = useMutation({
    mutationFn: () => api.post(`${base}/review`, {}),
    onSuccess: invalidate, onError: onErr,
  });
  const confirmM = useMutation({
    mutationFn: () => api.post(`${base}/confirm`, { acknowledge_gate_failures: ackGates }),
    onSuccess: () => { setMsg('Result confirmed.'); invalidate(); },
    onError: onErr,
  });
  const feedM = useMutation({
    mutationFn: () => api.post<{ levelCode: string; band: string; assets: Array<{ assetId: string }> }>(
      `${base}/feed`, { engagement_level_code: engagement || null }),
    onSuccess: (d) => {
      setMsg(`Fed ${d.assets.length} asset(s): vendor_readiness = ${d.levelCode.replace('_', ' ')}.`);
      invalidate(); invalidateAssets();
    },
    onError: onErr,
  });
  const reopenM = useMutation({
    mutationFn: () => api.post<{ state: string; cycle_number: number }>(
      `${base}/reopen`, { reason: 'Assessor reopened from console' },
    ),
    onSuccess: (r) => {
      // The server advances cycle_number as part of the reopen transition and
      // returns the new value, so the toast agrees with the Cycle chip that
      // invalidate() is about to refresh.
      const cycle = r?.cycle_number;
      setMsg(
        cycle
          ? `Reopened as cycle ${cycle} — download a fresh vendor copy to start it.`
          : 'Reopened — download a fresh vendor copy to start the next cycle.',
      );
      invalidate();
    },
    onError: onErr,
  });

  if (detailQ.isLoading || questionsQ.isLoading)
    return <p className="py-8 text-center text-ink-500 text-sm">Loading…</p>;
  if (detailQ.isError || !detailQ.data || !questionsQ.data)
    return <p className="py-8 text-center text-red-600 text-sm">Failed to load assessment.</p>;

  const d = detailQ.data;
  const answersBy = new Map(d.answers.map(a => [a.qid, a]));
  const scoresBy = new Map(d.scores.map(s => [s.qid, s]));
  const st = d.assessment.state;
  const findings = d.assessment.validation_report?.findings ?? [];
  const linkedCount = d.linkedAssetIds.length;
  const stage = stageIndex(st, linkedCount);
  const bandCls = d.result.band ? BAND_STYLE[d.result.band] ?? '' : 'bg-ink-50 text-ink-600 border-ink-200';
  const vendorAssets = assetsQ.data?.vendorSupplied ?? [];
  const candidates = assetsQ.data?.candidates ?? [];

  return (
    <div className="max-w-7xl mx-auto px-6 py-6 space-y-5">
      {/* Header */}
      <div>
        <Link className="text-xs text-ink-400 hover:text-accent" to={`/projects/${projectId}?tab=vendor-readiness`}>
          ← Vendor Readiness
        </Link>
        <div className="mt-2 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-lg font-medium text-ink-900">
              {d.assessment.vendor_name}
              {d.assessment.product_in_scope && (
                <span className="text-ink-500 font-normal"> — {d.assessment.product_in_scope}</span>
              )}
            </h1>
            <div className="mt-1 flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATE_CHIP[st] ?? 'bg-ink-100 text-ink-700'}`}>
                {st.replace('_', ' ')}
              </span>
              <span className="text-[11px] text-ink-400">Cycle {d.assessment.cycle_number}</span>
            </div>
          </div>
          <div className={`border rounded-sm px-4 py-2 text-right ${bandCls}`}>
            <div className="text-sm font-medium">{d.rating}</div>
            <div className="font-mono text-xs opacity-80">
              {d.result.achievedWeighted} / {d.result.maxWeighted} weighted
            </div>
          </div>
        </div>
      </div>

      {/* Workflow stepper */}
      <div className="flex items-center gap-1 text-[11px]">
        {STAGES.map((s, i) => (
          <div key={s} className="flex items-center gap-1">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${
              i < stage ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : i === stage ? 'bg-ink-900 border-ink-900 text-white'
              : 'bg-white border-ink-200 text-ink-400'}`}>
              <span className="font-medium">{i < stage ? '✓' : i + 1}</span>
              <span>{s}</span>
            </div>
            {i < STAGES.length - 1 && <div className={`w-4 h-px ${i < stage ? 'bg-emerald-300' : 'bg-ink-200'}`} />}
          </div>
        ))}
      </div>

      {msg && (
        <div className="border border-ink-200 rounded-sm bg-ink-50 px-3 py-2 text-xs text-ink-700 flex justify-between">
          <span>{msg}</span>
          <button className="text-ink-400 hover:text-ink-700" onClick={() => setMsg(null)}>✕</button>
        </div>
      )}

      {(st === 'DRAFT' || st === 'ISSUED' || st === 'REOPENED') && (
        <Card n="1" title="Issue to vendor">
          <div className="flex items-center gap-3 flex-wrap">
            <a href={apiUrl(`${base}/vendor-copy`)} target="_blank" rel="noreferrer">
              <button className="btn-primary text-xs">
                {st === 'REOPENED' ? 'Download fresh vendor copy (new cycle)' : 'Download vendor copy (.xlsx)'}
              </button>
            </a>
            {d.assessment.issued_fingerprint && (
              <span className="text-xs text-ink-500">
                Issued fingerprint <code className="font-mono">{d.assessment.issued_fingerprint.slice(0, 16)}…</code>
              </span>
            )}
          </div>
          <p className="text-xs text-ink-400">
            Send the workbook to the vendor; they complete Responses, Details, Evidence and the Declaration.
          </p>
        </Card>
      )}

      {(st === 'ISSUED' || st === 'IMPORT_FAILED' || st === 'REOPENED') && (
        <Card n="2" title="Upload returned submission">
          <input
            className="text-xs text-ink-700"
            type="file" accept=".xlsx"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadM.mutate(f); }}
          />
          {uploadM.isPending && <p className="text-xs text-ink-500">Parsing…</p>}
          {st === 'IMPORT_FAILED' && findings.length > 0 && (
            <ul className="text-xs text-red-600 space-y-1">
              {findings.map((f, i) => (
                <li key={i}>[{f.severity}] {f.code}{f.qid ? ` ${f.qid}` : ''}: {f.message}</li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {st !== 'DRAFT' && (
        <Card n="3" title={`Map vendor-supplied assets — ${linkedCount} linked`}>
          <p className="text-xs text-ink-500">
            The confirmed rating feeds the <span className="font-medium">Vendor Quantum Readiness</span> parameter
            of the assets mapped here. Only assets flagged “Vendor-supplied” in Supply Chain are mappable —
            flag more below without leaving this page.
          </p>

          {assetsQ.isLoading && <p className="text-xs text-ink-500">Loading assets…</p>}

          {vendorAssets.length > 0 && (
            <table className="w-full text-sm border border-ink-100 rounded-sm">
              <thead>
                <tr className="bg-ink-50 border-b border-ink-100">
                  <th className="px-3 py-2 w-8" />
                  <th className={th}>Vendor-supplied asset</th>
                  <th className={th}>Type</th>
                  <th className={th}>Current readiness</th>
                  <th className={th}>Engagement</th>
                </tr>
              </thead>
              <tbody>
                {vendorAssets.map((a) => (
                  <tr key={a.asset_id} className="border-b border-ink-100 last:border-0 hover:bg-ink-50/40">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        disabled={st === 'FED'}
                        checked={selected.has(a.asset_id)}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(a.asset_id); else next.delete(a.asset_id);
                          setSelected(next); setSelectionDirty(true);
                        }}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium text-sm text-ink-900">{a.name}</td>
                    <td className="px-3 py-2 text-xs text-ink-500">{a.asset_type ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-ink-700">{a.vendor_readiness?.replace('_', ' ') ?? '— not set —'}</td>
                    <td className="px-3 py-2 text-xs text-ink-700">{a.active_engagement?.replace('_', ' ') ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {st !== 'FED' && vendorAssets.length > 0 && (
            <button
              className="btn-secondary text-xs"
              disabled={!selectionDirty || saveLinksM.isPending}
              onClick={() => saveLinksM.mutate()}
            >
              Save mapping ({selected.size} selected)
            </button>
          )}
          {st === 'FED' && (
            <p className="text-xs text-ink-400">Mapping is frozen after feed — reopen the assessment to change it.</p>
          )}

          {candidates.length > 0 && st !== 'FED' && (
            <details className="text-xs">
              <summary className="cursor-pointer text-ink-500 hover:text-ink-700">
                Other project assets not yet flagged vendor-supplied ({candidates.length})
              </summary>
              <table className="w-full text-sm border border-ink-100 rounded-sm mt-2">
                <tbody>
                  {candidates.map((a) => (
                    <tr key={a.asset_id} className="border-b border-ink-100 last:border-0">
                      <td className="px-3 py-2 text-sm text-ink-900">{a.name}</td>
                      <td className="px-3 py-2 text-xs text-ink-500">{a.asset_type ?? '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          className="btn-secondary text-xs"
                          disabled={flagM.isPending}
                          onClick={() => flagM.mutate(a.asset_id)}
                        >
                          Mark vendor-supplied
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
          {vendorAssets.length === 0 && candidates.length === 0 && !assetsQ.isLoading && (
            <p className="text-xs text-ink-500">No assets enrolled in this project yet — enroll assets first.</p>
          )}
        </Card>
      )}

      {st === 'IMPORTED' && (
        <Card n="4" title="Score">
          <button className="btn-primary text-xs" onClick={() => reviewM.mutate()}>Start review</button>
        </Card>
      )}

      {(st === 'UNDER_REVIEW' || st === 'CONFIRMED' || st === 'FED') && (
        <Card n="4" title="Review & score">
          {findings.length > 0 && (
            <details className="text-xs text-ink-700">
              <summary className="cursor-pointer text-ink-500">Import findings ({findings.length})</summary>
              <ul className="mt-2 space-y-1">
                {findings.map((f, i) => (
                  <li key={i}>[{f.severity}] {f.code}{f.qid ? ` ${f.qid}` : ''}: {f.message}</li>
                ))}
              </ul>
            </details>
          )}
          <table className="w-full text-sm border border-ink-100 rounded-sm">
            <thead>
              <tr className="bg-ink-50 border-b border-ink-100">
                <th className={th}>Q</th>
                <th className={th}>Question</th>
                <th className={th}>Vendor response</th>
                <th className={th}>Details</th>
                <th className="text-right text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">Suggested</th>
                <th className={th}>Score</th>
              </tr>
            </thead>
            <tbody>
              {questionsQ.data.map((q, idx) => {
                const a = answersBy.get(q.qid);
                const s = scoresBy.get(q.qid);
                return (
                  <tr key={q.qid} className={`border-b border-ink-100 last:border-0 align-top ${idx % 2 ? 'bg-ink-50/30' : ''}`}>
                    <td className="px-3 py-2 font-mono text-xs text-ink-900 whitespace-nowrap">
                      {q.qid}{q.is_gate ? <span title="Gate question — cannot be N/A; a 0 floors the rating" className="text-red-500"> ⚑</span> : null}
                    </td>
                    <td className="px-3 py-2 text-ink-700 leading-relaxed" title={q.guidance_assessor}>{q.question}</td>
                    <td className="px-3 py-2 text-xs text-ink-700 whitespace-nowrap">{a?.response ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-ink-500 max-w-md">{a?.details ?? ''}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{s?.suggested ?? '—'}</td>
                    <td className="px-3 py-2">
                      <select
                        className="border border-ink-200 rounded-sm px-1 py-0.5 text-xs focus:outline-none focus:border-accent/50"
                        disabled={st !== 'UNDER_REVIEW'}
                        value={s?.score ?? ''}
                        onChange={(e) => scoreM.mutate({ qid: q.qid, score: e.target.value || null })}
                      >
                        <option value="">—</option>
                        {SCORE_OPTIONS.filter(o => !(q.is_gate && o === 'NA')).map(o => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {st === 'UNDER_REVIEW' && (
        <Card n="5" title="Confirm">
          {!d.fullyAddressed && (
            <p className="text-xs text-ink-500">
              All questions must be scored before confirming — provisional results are never confirmed.
            </p>
          )}
          {d.result.gateFailures.length > 0 && (
            <label className="flex items-start gap-2 text-xs text-red-600">
              <input type="checkbox" checked={ackGates} onChange={(e) => setAckGates(e.target.checked)} />
              <span>
                Acknowledge gate failure on {d.result.gateFailures.join(', ')} — confirming will feed
                Very low readiness to the mapped assets.
              </span>
            </label>
          )}
          <button
            className="btn-primary text-xs"
            disabled={!d.fullyAddressed || confirmM.isPending}
            onClick={() => confirmM.mutate()}
          >
            Confirm result
          </button>
        </Card>
      )}

      {st === 'CONFIRMED' && (
        <Card n="6" title="Feed Supply Chain" tone="success">
          <p className="text-xs text-ink-500">
            Writes <span className="font-medium">vendor_readiness</span> (and optionally
            <span className="font-medium"> active_engagement</span>) for the {linkedCount} mapped asset(s) —
            one feed event per asset.
          </p>
          {linkedCount === 0 && (
            <p className="text-xs text-red-600">No assets mapped — map assets in step 3 first.</p>
          )}
          <label className="text-xs text-ink-700 block">
            <span className="block mb-1">Active engagement (assessor judgment, optional)</span>
            <select
              className="border border-ink-200 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-accent/50"
              value={engagement} onChange={(e) => setEngagement(e.target.value)}
            >
              <option value="">— not set —</option>
              {LEVELS.map(l => <option key={l} value={l}>{l.replace('_', ' ')}</option>)}
            </select>
          </label>
          <button
            className="btn-primary text-xs"
            disabled={linkedCount === 0 || feedM.isPending}
            onClick={() => feedM.mutate()}
          >
            Feed {linkedCount} asset(s)
          </button>
        </Card>
      )}

      {(st === 'CONFIRMED' || st === 'FED' || st === 'IMPORT_FAILED' || st === 'INVALID') && (
        <section className="flex justify-end">
          <button className="btn-secondary text-xs" onClick={() => reopenM.mutate()}>
            Reopen for a new cycle
          </button>
        </section>
      )}
    </div>
  );
}
